import { InjectionToken } from '@angular/core';
import { LoadStatus } from '../load-status';
import {
    LOCATION_TRACKER_STATE_STORE_KEY,
    LOCATION_TRACKER_STORE_KEY,
} from '../location-tracker/location-tracker.state';

/**
 * Feature key under which the user-store slice is registered in the root store.
 */
export const USER_STORE_FEATURE_KEY = 'userStore';

/**
 * The user-store keys this slice synchronises with Azure. The Azure user store is a flat per-user
 * namespace that also holds the persisted NgRx slices of the device handover (see
 * `ConcurrentUsageService`), so the slice names its own keys instead of reading everything:
 * `loadUserStore` requests only these, and the device check-out (`ConcurrentUsageService.uploadAll`)
 * uploads them again, in case an immediate upload after `setUserStoreValue` never landed. A key
 * written with `setUserStoreValue` but missing here still goes to Azure immediately, but is never
 * loaded back and not re-sent on check-out — add every new key here.
 */
export const USER_STORE_SYNCED_KEYS: readonly string[] = [LOCATION_TRACKER_STORE_KEY, LOCATION_TRACKER_STATE_STORE_KEY];

/**
 * The mirrored entries, keyed by store key, holding each entry's `data` payload only — the slice
 * does not mirror `gid`/`createdAt`/`modifiedAt`; nothing in the client reads them, and every
 * write recomputes them the same way the backend does. A key listed in
 * {@link USER_STORE_UNMIRRORED_KEYS} never appears here.
 */
export type UserStoreEntriesState = Readonly<Record<string, unknown>>;

/**
 * `status`/`hasBackendSettled` follow the same split as `LocationTrackerState`: `status` also
 * flips to `Loaded` on a cache rehydration, so an offline read has somewhere to look immediately,
 * while `hasBackendSettled` is the stricter "the network reply has landed, successfully or not"
 * question — set only by {@link UserStoreEffects}' backend load, never by the cache bridge. A
 * caller that must tell a cache hit from a confirmed-current value waits on that flag instead.
 */
export type UserStoreState = Readonly<{
    entries: UserStoreEntriesState;
    status: LoadStatus;
    hasBackendSettled: boolean;
    /**
     * Keys whose DELETE has not been confirmed by the backend yet. A delete bypasses the offline
     * request queue on purpose — see `UserStoreApiService.deleteEntry`: the queue replays every
     * request with `httpClient.post(...)`, so a queued DELETE would arrive at the backend as a
     * bodiless POST and sit there failing forever — so this is that retry mechanism's own queue
     * instead. A key is added here the moment a delete is requested for a mirrored key (alongside
     * removing it from `entries`, see `unmirrorUserStoreEntryReducer`) and removed once the
     * backend confirms the delete, including a 404 (the key is already gone, which is exactly what
     * a delete wants — see `UserStoreApiService.deleteEntry`). Keys in `USER_STORE_UNMIRRORED_KEYS`
     * are never tracked here: their delete already goes straight to Azure with no mirror step, and
     * a failure surfaces through `deleteUserStoreValueFailed` alone.
     */
    pendingDeletes: readonly string[];
}>;

/**
 * The shape written to IndexedDB by the generic persistence layer (see `PERSISTED_SLICES` in
 * `user-store-store.module.ts`) and read back by `UserStoreEffects.applyHydratedUserStore$`. It
 * mirrors `pendingDeletes` alongside the entries so an offline delete survives a restart the same
 * way an offline upsert does — see `pendingDeletes`'s own doc comment above.
 */
export type UserStorePersistedState = Readonly<{
    entries: UserStoreEntriesState;
    pendingDeletes: readonly string[];
}>;

/** Default-entity factory for the mirrored entries — nothing loaded yet. */
export function createDefaultUserStoreEntriesState(): UserStoreEntriesState {
    return {};
}

/** Default-entity factory for the persisted projection — nothing loaded and nothing pending. */
export function createDefaultUserStorePersistedState(): UserStorePersistedState {
    return { entries: createDefaultUserStoreEntriesState(), pendingDeletes: [] };
}

export const initialUserStoreState: UserStoreState = {
    entries: createDefaultUserStoreEntriesState(),
    status: LoadStatus.Idle,
    hasBackendSettled: false,
    pendingDeletes: [],
};

/**
 * Injection token for the keys excluded from the client-side mirror. An excluded key is never
 * written into this slice, is never part of what `UserStoreStoreModule` persists to IndexedDB,
 * and its reads/writes go straight to Azure through `UserStoreApiService`
 * instead of round-tripping through the store. `UserStoreEffects` reads it to decide, per action,
 * whether to mirror a bulk-loaded entry or a `setUserStoreValue`/`deleteUserStoreValue` write; the
 * slice's persisted-projection selector re-applies the same exclusion as a second line of defence
 * (see `createPersistedUserStoreSelector`), so a bug in the effects cannot leak an excluded
 * key into storage.
 *
 * `UserStoreStoreModule` provides a default of `[]`, so an app that mirrors everything needs no
 * extra wiring. An app that must keep some keys out overrides it downstream, e.g. in its own root
 * module, after importing `UserStoreStoreModule`:
 * ```ts
 * providers: [{ provide: USER_STORE_UNMIRRORED_KEYS, useValue: ['someSensitiveKey'] }]
 * ```
 */
export const USER_STORE_UNMIRRORED_KEYS = new InjectionToken<string[]>('USER_STORE_UNMIRRORED_KEYS');
